import { useLayoutContext } from "../context/LayoutContext";
import { useAuth } from "../context/AuthContext";
import React, { useState } from "react";
import Image from "../components/common/Image";
import api from "../hooks/api";
import { API_URLS } from "../App";
import { ToggleLeft } from "lucide-react";
import { MuiColorInput } from "mui-color-input";
import Tabs from "@mui/material/Tabs";
import Box from "@mui/material/Box";
import { Tab } from "@mui/material";

interface MiniSiteConfiguration {
  erp_user_reference: string;
  minisite_enabled: boolean;
  minisite_associated_user: string | null;
  minisite_iframe_enabled: boolean;
  minisite_hostname: string;
  minisite_cross_domain: string | null;
  custom_title: string | null;
  custom_subtitle: string | null;
  custom_logo_location: string | null;
  custom_favicon_location: string | null;
  custom_css: string | null;
  custom_js: string | null;
  custom_welcome_message: string | null;
  custom_footer_text: string | null;
  custom_primary_color: string | null;
  custom_secondary_color: string | null;
  custom_success_color: string | null;
  custom_background_color: string | null;
  custom_background_secondary_color: string | null;
  custom_text_color: string | null;
  custom_text_light_color: string | null;
  custom_button_text_color: string | null;
  custom_button_color: string | null;
  custom_button_hover_color: string | null;
  custom_button_active_color: string | null;
  custom_link_text_color: string | null;
  custom_link_hover_color: string | null;
  custom_link_active_color: string | null;
  custom_button_style: string | null;
  analytics_enabled: boolean;
  analytics_tracking_id: string | null;
  analytics_custom_js: string | null;
  seo_meta_title: string | null;
  seo_meta_description: string | null;
  seo_meta_keywords: string | null;
  contact_email: string;
  contact_phone: string;
  contact_address: string | null;
  contact_map_embed_code: string | null;
  contact_facebook: string | null;
  contact_twitter: string | null;
  contact_instagram: string | null;
  contact_linkedin: string | null;
  contact_youtube: string | null;
  contact_whatsapp: string | null;
  enable_custom_contact_html: boolean | null;
  contact_custom_html: string | null;

  checkout_mode: string | null;
  enable_custom_checkout_html: boolean | null;
  checkout_custom_html: string | null;
  custom_checkout_success_message: string | null;

  version: number;
}

const MiniSiteSettings = () => {
  const {
    userSettings,
    setUserSettings,
    systemError,
    setSystemError,
    loading,
    setLoading,
    error,
    setError,
    success,
    setSuccess,
    modalScreen,
    setModalScreen,
    page,
    setPage,
  } = useLayoutContext();
  const { saveProfile } = useAuth();
  const [internalUserSettings, setInternalUserSettings] = useState(
    userSettings ||
      ({
        generalSettings: {} as GeneralSettings,
        profile: {} as Profile,
      } as ProfileAndSettings),
  );
  const [file, setFile] = useState<File | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [configuration, setConfiguration] =
    useState<MiniSiteConfiguration | null>(null);
  const [collapsedFielsets, setCollapsedFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [tabsEnabled, setTabsEnabled] = useState<{ [key: string]: number }>({});

  const fetchUsers = async () => {
    const filterUserRef =
      configuration?.erp_user_reference || userSettings?.client?.id;
    const availableUsersResponse = await api.get(
      API_URLS.USERS + "?erp_user_reference=" + filterUserRef,
    );
    setUsers(availableUsersResponse.data.users || []);
  };

  const fetchMiniSiteSettings = async () => {
    const response = await api.get(API_URLS.MINISITE_CONFIG);
    setConfiguration(response.data as MiniSiteConfiguration);
  };

  const saveMiniSiteSettings = async () => {
    if (!configuration) return;

    const response = await api.put(API_URLS.MINISITE_CONFIG, configuration);
    setConfiguration(response.data as MiniSiteConfiguration);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    saveMiniSiteSettings()
      .then(async () => {
        if (file) {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("data", JSON.stringify(formData));
          return await api.put(
            API_URLS.MINISITE_CONFIG + "/custom_logo_location",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            },
          );
        }
        setSuccess("Configurações do MiniSite salvas com sucesso.");
      })
      .catch((error) => {
        setError("Erro ao salvar configurações do MiniSite: " + error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    setLoading(true);
    fetchMiniSiteSettings()
      .then(() => {
        fetchUsers().catch((err) => {
          setError("Erro ao carregar utilizadores: " + err.message);
        });
      })
      .catch((err) => {
        setError("Erro ao carregar configuração do MiniSite: " + err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const renderColorCustomInput = (
    label: string,
    id: keyof MiniSiteConfiguration,
    defaultColor: string | null,
    configuration: MiniSiteConfiguration,
  ) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <div>
          <label
            className="block text-sm font-medium text-gray-900"
            htmlFor={id}
          >
            {label}
          </label>
        </div>
        <div>
          <MuiColorInput
            format="hsl"
            id={id}
            value={configuration[id]?.toString() || defaultColor || ""}
            onChange={(color) =>
              setConfiguration({
                ...configuration,
                [id]: color,
              })
            }
            className="w-full"
            size="small"
          />
        </div>
      </div>
    );
  };

  const renderInput = (
    id: keyof MiniSiteConfiguration,
    label: string,
    tip: string,
    value: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    type:
      | "text"
      | "checkbox"
      | "email"
      | "number"
      | "password"
      | "url"
      | "tel" = "text",
  ) => (
    <div className="space-y-2">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <label
            className="block text-sm font-medium text-gray-900"
            htmlFor={id}
          >
            {label}
          </label>
          {tip && <p className="text-xs text-gray-500 mt-1">{tip}</p>}
        </div>
      </div>
      <div className="mt-2">
        {type === "checkbox" ? (
          <label className="inline-flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              id={id}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              checked={value === "Sim"}
              onChange={onChange}
            />
            <span className="text-sm text-gray-700">Ativar</span>
          </label>
        ) : (
          <input
            type={type}
            id={id}
            className="block w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={value}
            onChange={onChange}
          />
        )}
      </div>
    </div>
  );

  const renderTextarea = (
    id: keyof MiniSiteConfiguration,
    label: string,
    tip: string,
    value: string,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
    rows: number = 3,
  ) => (
    <div className="space-y-2">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <label
            className="block text-sm font-medium text-gray-900"
            htmlFor={id}
          >
            {label}
          </label>
          {tip && <p className="text-xs text-gray-500 mt-1">{tip}</p>}
        </div>
      </div>
      <div className="mt-2">
        <textarea
          rows={rows}
          id={id}
          className="block w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-y"
          value={value}
          onChange={onChange}
        />
      </div>
    </div>
  );

  if (!userSettings || !configuration) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex flex-col gap-6">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <span className="text-3xl font-bold text-gray-900 tracking-tight">
                Configurações do MiniSite
              </span>
              <p className="text-gray-500 mt-1">
                Personalize a aparência e funcionalidades do seu MiniSite
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="overflow-auto">
            {loading ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  <p className="mt-4 text-gray-600">
                    A carregar configurações...
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <fieldset
                    className={`collapsible ${collapsedFielsets["generalSettings"] ? "is-collapsed" : ""}`}
                  >
                    <legend
                      className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-200"
                      onClick={() =>
                        setCollapsedFields({
                          ...collapsedFielsets,
                          generalSettings:
                            !collapsedFielsets["generalSettings"],
                        })
                      }
                    >
                      <h2 className="text-xl font-semibold text-gray-900">
                        Configurações Gerais
                      </h2>
                      <ToggleLeft
                        className={`w-6 h-6 text-gray-400 transition-transform ${collapsedFielsets["generalSettings"] ? "rotate-180" : ""}`}
                      />
                    </legend>
                    <main className="p-6 space-y-6">
                      {renderInput(
                        "minisite_enabled",
                        "MiniSite Ativo",
                        "Ativa ou desativa o MiniSite.",
                        configuration.minisite_enabled ? "Sim" : "Não",
                        (e) =>
                          setConfiguration({
                            ...configuration,
                            minisite_enabled: e.target.checked,
                          }),
                        "checkbox",
                      )}

                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label
                              className="block text-sm font-medium text-gray-900"
                              htmlFor="associated_user_id"
                            >
                              Utilizador associado
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              O utilizador associado ao MiniSite.
                              <br />
                              Este utilizador será utilizado para criação de
                              propostas; recomendados um utilizador guest.
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <select
                            id="associated_user_id"
                            className="block w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={configuration.minisite_associated_user || ""}
                            onChange={(e) =>
                              setConfiguration({
                                ...configuration,
                                minisite_associated_user: e.target.value,
                              })
                            }
                          >
                            {users.length === 0 ? (
                              <option disabled value="">
                                Nenhum utilizador disponível
                              </option>
                            ) : (
                              <option disabled value="">
                                Selecione um utilizador
                              </option>
                            )}
                            {users?.map((user) => (
                              <option key={user.id} value={user.username}>
                                {user.firstName} {user.lastName} ({user.type})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {renderInput(
                        "minisite_hostname",
                        "Site Customizado",
                        "Endereço do MiniSite no subdomínio iapc.pt.",
                        configuration.minisite_hostname,
                        (e) =>
                          setConfiguration({
                            ...configuration,
                            minisite_hostname: e.target.value,
                          }),
                      )}
                      {renderInput(
                        "minisite_cross_domain",
                        "Domínio Customizado",
                        "Domínio customizado para o MiniSite. Deve ser um domínio válido e estar configurado para apontar para o subdomínio do iapc.pt.",
                        configuration.minisite_cross_domain || "",
                        (e) =>
                          setConfiguration({
                            ...configuration,
                            minisite_cross_domain: e.target.value,
                          }),
                      )}
                      {renderInput(
                        "minisite_iframe_enabled",
                        "Usar Iframe",
                        "Se habilitado, o microsite será carregado dentro de um iframe.",
                        configuration.minisite_iframe_enabled ? "Sim" : "Não",
                        (e) =>
                          setConfiguration({
                            ...configuration,
                            minisite_iframe_enabled: e.target.checked,
                          }),
                        "checkbox",
                      )}
                    </main>
                  </fieldset>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <fieldset
                    className={`collapsible ${collapsedFielsets["customContacts"] ? "is-collapsed" : ""}`}
                  >
                    <legend
                      className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-200"
                      onClick={() =>
                        setCollapsedFields({
                          ...collapsedFielsets,
                          customContacts: !collapsedFielsets["customContacts"],
                        })
                      }
                    >
                      <h2 className="text-xl font-semibold text-gray-900">
                        Contactos
                      </h2>
                      <ToggleLeft
                        className={`w-6 h-6 text-gray-400 transition-transform ${collapsedFielsets["customContacts"] ? "rotate-180" : ""}`}
                      />
                    </legend>
                    <main className="p-6 space-y-6">
                      <Box
                        sx={{
                          borderBottom: 1,
                          marginBottom: 4,
                          borderColor: "divider",
                        }}
                      >
                        <Tabs
                          value={
                            typeof tabsEnabled["contacts"] === "number"
                              ? (tabsEnabled["contacts"] ?? 0)
                              : 0
                          }
                          onChange={(event, newValue) => {
                            setTabsEnabled({
                              ...tabsEnabled,
                              ["contacts"]: newValue,
                            });
                          }}
                          aria-label="contacts-mode"
                        >
                          <Tab
                            label="normal"
                            aria-controls="tab-contacts-normal"
                            id="tabpanel-contacts-normal"
                            tabIndex={0}
                          />
                          <Tab
                            label="avançado"
                            aria-controls="tab-contacts-advance"
                            id="tabpanel-contact-advance"
                            tabIndex={1}
                          />
                        </Tabs>
                      </Box>

                      <div
                        role="tabpanel"
                        hidden={(tabsEnabled["contacts"] ?? 0) !== 0}
                        id="tab-contacts-normal"
                        aria-labelledby="tabpanel-contacts-normal"
                        tabIndex={0}
                      >
                        {renderTextarea(
                          "contact_address",
                          "Morada de Contacto",
                          "A morada de contacto será utilizada na página de contactos.",
                          configuration.contact_address || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              contact_address: e.target.value,
                            }),
                        )}

                        {renderTextarea(
                          "contact_map_embed_code",
                          "Código de Incorporação do Mapa",
                          "O código de incorporação do mapa será utilizado na página de contactos.",
                          configuration.contact_map_embed_code || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              contact_map_embed_code: e.target.value,
                            }),
                          5,
                        )}

                        {renderInput(
                          "contact_email",
                          "Email de Contacto",
                          "O email de contacto será utilizado na página de contactos.",
                          configuration.contact_email || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              contact_email: e.target.value,
                            }),
                        )}

                        {renderInput(
                          "contact_phone",
                          "Telefone de Contacto",
                          "O telefone de contacto será utilizado na página de contactos.",
                          configuration.contact_phone || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              contact_phone: e.target.value,
                            }),
                        )}

                        {renderInput(
                          "contact_whatsapp",
                          "WhatsApp",
                          "Link para o WhatsApp da empresa.",
                          configuration.contact_whatsapp || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              contact_whatsapp: e.target.value,
                            }),
                        )}

                        {renderInput(
                          "contact_facebook",
                          "Facebook",
                          "Link para o Facebook da empresa.",
                          configuration.contact_facebook || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              contact_facebook: e.target.value,
                            }),
                        )}

                        {renderInput(
                          "contact_twitter",
                          "Twitter",
                          "Link para o Twitter da empresa.",
                          configuration.contact_twitter || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              contact_twitter: e.target.value,
                            }),
                        )}

                        {renderInput(
                          "contact_instagram",
                          "Instagram",
                          "Link para o Instagram da empresa.",
                          configuration.contact_instagram || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              contact_instagram: e.target.value,
                            }),
                        )}

                        {renderInput(
                          "contact_linkedin",
                          "LinkedIn",
                          "Link para o LinkedIn da empresa.",
                          configuration.contact_linkedin || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              contact_linkedin: e.target.value,
                            }),
                        )}

                        {renderInput(
                          "contact_youtube",
                          "YouTube",
                          "Link para o YouTube da empresa.",
                          configuration.contact_youtube || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              contact_youtube: e.target.value,
                            }),
                        )}
                      </div>
                      <div
                        role="tabpanel"
                        hidden={tabsEnabled["contacts"] !== 1}
                        id="tab-contacts-advance"
                        aria-labelledby="tabpanel-contacts-advance"
                        tabIndex={1}
                      >
                        {renderInput(
                          "enable_custom_contact_html",
                          "Habilitar HTML Customizado",
                          "Habilita o uso de HTML customizado na página de contactos.",
                          configuration.enable_custom_contact_html
                            ? "Sim"
                            : "Não",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              enable_custom_contact_html: e.target.checked,
                            }),
                          "checkbox",
                        )}

                        {renderTextarea(
                          "contact_custom_html",
                          "HTML Customizado",
                          "O HTML customizado será utilizado na página de contactos.",
                          configuration.contact_custom_html || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              contact_custom_html: e.target.value,
                            }),
                          15,
                        )}
                      </div>
                    </main>
                  </fieldset>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <fieldset
                    className={`collapsible ${collapsedFielsets["customSettings"] ? "is-collapsed" : ""}`}
                  >
                    <legend
                      className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-200"
                      onClick={() =>
                        setCollapsedFields({
                          ...collapsedFielsets,
                          customSettings: !collapsedFielsets["customSettings"],
                        })
                      }
                    >
                      <h2 className="text-xl font-semibold text-gray-900">
                        Aparência
                      </h2>
                      <ToggleLeft
                        className={`w-6 h-6 text-gray-400 transition-transform ${collapsedFielsets["customSettings"] ? "rotate-180" : ""}`}
                      />
                    </legend>
                    <main className="p-6 space-y-6">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label
                              className="block text-sm font-medium text-gray-900"
                              htmlFor="logoUrl"
                            >
                              Logótipo a exibir no site
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              O logotipo será utilizado no cabeçalho do
                              MiniSite.
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <div className="flex items-center gap-4">
                            <label className="flex items-center px-4 py-2.5 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                              <input
                                className="hidden"
                                type="file"
                                name="companyLogo"
                                accept="image/*"
                                onChange={(e) => {
                                  const selectedFile = e.target.files?.[0];
                                  if (selectedFile) {
                                    setFile(selectedFile);
                                  }
                                }}
                              />
                              <span className="text-sm font-medium text-gray-700">
                                Escolher ficheiro...
                              </span>
                            </label>
                            <span className="text-sm text-gray-600">
                              {file ? file.name : "Nenhum ficheiro selecionado"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-900">
                              Pré-visualização do Logótipo
                            </label>
                          </div>
                        </div>
                        <div className="mt-2">
                          {file ? (
                            <div className="relative inline-block">
                              <picture>
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt="Selected Logo"
                                  className="rounded-lg border border-gray-200"
                                  style={{
                                    maxWidth: "200px",
                                    maxHeight: "100px",
                                  }}
                                />
                              </picture>
                              <span className="absolute top-2 right-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                Preview
                              </span>
                            </div>
                          ) : configuration.custom_logo_location ? (
                            <div className="inline-block max-w-[150px] max-h-[100px]">
                              <Image
                                src="http://iapc.pt/images/150/logo-IAPC.jpg"
                                dataSrc={configuration.custom_logo_location}
                                loading="progressive"
                                sizes="150"
                                alt="Logo"
                                className="object-cover rounded-lg border border-gray-200"
                              />
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">
                              Nenhum logotipo selecionado
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label
                              className="block text-sm font-medium text-gray-900"
                              htmlFor="custom_title"
                            >
                              Nome a exibir no site
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              O nome será utilizado no cabeçalho do MiniSite.
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <input
                            type="text"
                            id="custom_title"
                            className="block w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={configuration.custom_title || ""}
                            onChange={(e) =>
                              setConfiguration({
                                ...configuration,
                                custom_title: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label
                              className="block text-sm font-medium text-gray-900"
                              htmlFor="custom_subtitle"
                            >
                              Subtítulo a exibir no site
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              O subtítulo será utilizado no cabeçalho do
                              MiniSite.
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <input
                            type="text"
                            id="custom_subtitle"
                            className="block w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={configuration.custom_subtitle || ""}
                            onChange={(e) =>
                              setConfiguration({
                                ...configuration,
                                custom_subtitle: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label
                              className="block text-sm font-medium text-gray-900"
                              htmlFor="custom_welcome_message"
                            >
                              Mensagem de Boas Vindas a exibir no site
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              A mensagem de boas vindas será utilizada na página
                              inicial do MiniSite.
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <textarea
                            rows={5}
                            id="custom_welcome_message"
                            className="block w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-y"
                            value={configuration.custom_welcome_message || ""}
                            onChange={(e) =>
                              setConfiguration({
                                ...configuration,
                                custom_welcome_message: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label
                              className="block text-sm font-medium text-gray-900"
                              htmlFor="custom_footer_text"
                            >
                              Texto de Rodapé a exibir no site
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              O texto de rodapé será utilizado no rodapé do
                              MiniSite.
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <textarea
                            rows={3}
                            id="custom_footer_text"
                            className="block w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-y"
                            value={configuration.custom_footer_text || ""}
                            onChange={(e) =>
                              setConfiguration({
                                ...configuration,
                                custom_footer_text: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    </main>
                  </fieldset>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <fieldset
                    className={`collapsible ${collapsedFielsets["customThemeSettings"] ? "is-collapsed" : ""}`}
                  >
                    <legend
                      className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-200"
                      onClick={() =>
                        setCollapsedFields({
                          ...collapsedFielsets,
                          customThemeSettings:
                            !collapsedFielsets["customThemeSettings"],
                        })
                      }
                    >
                      <h2 className="text-xl font-semibold text-gray-900">
                        Tema e Cores
                      </h2>
                      <ToggleLeft
                        className={`w-6 h-6 text-gray-400 transition-transform ${collapsedFielsets["customThemeSettings"] ? "rotate-180" : ""}`}
                      />
                    </legend>
                    <main className="p-6 space-y-6">
                      <Box
                        sx={{
                          borderBottom: 1,
                          marginBottom: 4,
                          borderColor: "divider",
                        }}
                      >
                        <Tabs
                          value={
                            typeof tabsEnabled["theme"] === "number"
                              ? (tabsEnabled["theme"] ?? 0)
                              : 0
                          }
                          onChange={(event, newValue) => {
                            setTabsEnabled({
                              ...tabsEnabled,
                              ["theme"]: newValue,
                            });
                          }}
                          aria-label="theme-mode"
                        >
                          <Tab
                            label="normal"
                            aria-controls="tab-theme-normal"
                            id="tabpanel-theme-normal"
                            tabIndex={0}
                          />
                          <Tab
                            label="avançado"
                            aria-controls="tab-theme-advance"
                            id="tabpanel-theme-advance"
                            tabIndex={1}
                          />
                        </Tabs>
                      </Box>

                      <div
                        role="tabpanel"
                        hidden={(tabsEnabled["theme"] ?? 0) !== 0}
                        id="tab-theme-normal"
                        aria-labelledby="tabpanel-theme-normal"
                        tabIndex={0}
                      >
                        {renderColorCustomInput(
                          "Cor de Fundo",
                          "custom_background_color",
                          "#ffffff",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor de Fundo Secundária",
                          "custom_background_secondary_color",
                          "#f7f8fd",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor do Texto",
                          "custom_text_color",
                          "#232a4a",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor do Texto Claro",
                          "custom_text_light_color",
                          "#6e7be6",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor do Texto dos Botões",
                          "custom_button_text_color",
                          "#232a4a",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor Primária",
                          "custom_primary_color",
                          "#232a4a",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor Secundária",
                          "custom_secondary_color",
                          "#6e7be6",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor de Sucesso",
                          "custom_success_color",
                          "#3adca7",
                          configuration,
                        )}

                        {renderColorCustomInput(
                          "Cor dos Botões",
                          "custom_button_color",
                          "#00d1b2",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor do Botão ao Passar o Mouse",
                          "custom_button_hover_color",
                          "#00b89c",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor do Botão ao Clicar",
                          "custom_button_active_color",
                          "#009e87",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor do Link",
                          "custom_link_text_color",
                          "#3273dc",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor do Link ao Passar o Mouse",
                          "custom_link_hover_color",
                          "#2759a5",
                          configuration,
                        )}
                        {renderColorCustomInput(
                          "Cor do Link ao Clicar",
                          "custom_link_active_color",
                          "#1c3d73",
                          configuration,
                        )}
                      </div>
                      <div
                        role="tabpanel"
                        hidden={tabsEnabled["theme"] !== 1}
                        id="tab-theme-advance"
                        aria-labelledby="tabpanel-theme-advance"
                        tabIndex={1}
                      >
                        {renderTextarea(
                          "custom_css",
                          "CSS Avançado",
                          "O CSS avançado será adicionado ao final do ficheiro CSS.",
                          configuration.custom_css || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              custom_css: e.target.value,
                            }),
                          15,
                        )}

                        {renderTextarea(
                          "custom_js",
                          "JS Avançado",
                          "O JS avançado será adicionado ao final do ficheiro JS.",
                          configuration.custom_js || "",
                          (e) =>
                            setConfiguration({
                              ...configuration,
                              custom_js: e.target.value,
                            }),
                          15,
                        )}
                      </div>
                    </main>
                  </fieldset>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <fieldset
                    className={`collapsible ${collapsedFielsets["customSeo"] ? "is-collapsed" : ""}`}
                  >
                    <legend
                      className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-200"
                      onClick={() =>
                        setCollapsedFields({
                          ...collapsedFielsets,
                          customSeo: !collapsedFielsets["customSeo"],
                        })
                      }
                    >
                      <h2 className="text-xl font-semibold text-gray-900">
                        SEO e Analytics
                      </h2>
                      <ToggleLeft
                        className={`w-6 h-6 text-gray-400 transition-transform ${collapsedFielsets["customSeo"] ? "rotate-180" : ""}`}
                      />
                    </legend>
                    <main className="p-6 space-y-6">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label
                              className="block text-sm font-medium text-gray-900"
                              htmlFor="seo_meta_title"
                            >
                              Meta Título
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              O meta título será utilizado para SEO.
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <input
                            type="text"
                            id="seo_meta_title"
                            className="block w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={configuration.seo_meta_title || ""}
                            onChange={(e) =>
                              setConfiguration({
                                ...configuration,
                                seo_meta_title: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label
                              className="block text-sm font-medium text-gray-900"
                              htmlFor="seo_meta_description"
                            >
                              Meta Descrição
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              A meta descrição será utilizada para SEO.
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <textarea
                            rows={3}
                            id="seo_meta_description"
                            className="block w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-y"
                            value={configuration.seo_meta_description || ""}
                            onChange={(e) =>
                              setConfiguration({
                                ...configuration,
                                seo_meta_description: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label
                              className="block text-sm font-medium text-gray-900"
                              htmlFor="seo_meta_keywords"
                            >
                              Meta Keywords
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              As meta keywords serão utilizadas para SEO.
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <input
                            type="text"
                            id="seo_meta_keywords"
                            className="block w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={configuration.seo_meta_keywords || ""}
                            onChange={(e) =>
                              setConfiguration({
                                ...configuration,
                                seo_meta_keywords: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label
                              className="block text-sm font-medium text-gray-900"
                              htmlFor="analytics_enabled"
                            >
                              Ativar Google Analytics
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Ativa ou desativa o Google Analytics.
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <label className="inline-flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              id="analytics_enabled"
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                              checked={configuration.analytics_enabled || false}
                              onChange={(e) =>
                                setConfiguration({
                                  ...configuration,
                                  analytics_enabled: e.target.checked,
                                })
                              }
                            />
                            <span className="text-sm text-gray-700">
                              Habilitar Google Analytics
                            </span>
                          </label>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <label
                              className="block text-sm font-medium text-gray-900"
                              htmlFor="google_analytics_id"
                            >
                              Google Analytics ID
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              O Google Analytics ID será utilizado para
                              rastreamento.
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <input
                            type="text"
                            id="google_analytics_id"
                            className="block w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                            required={configuration.analytics_enabled || false}
                            placeholder="UA-XXXXX-Y ou G-XXXXXXX"
                            readOnly={!configuration.analytics_enabled || false}
                            value={configuration.analytics_tracking_id || ""}
                            onChange={(e) =>
                              setConfiguration({
                                ...configuration,
                                analytics_tracking_id: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    </main>
                  </fieldset>
                </div>

                {/* Submit Button */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">
                        Não se esqueça de guardar as suas alterações
                      </p>
                    </div>
                    <button
                      type="submit"
                      className={`px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors ${
                        loading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      disabled={loading || !!error}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <svg
                            className="animate-spin h-5 w-5"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          A guardar...
                        </span>
                      ) : (
                        "Guardar Configurações"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MiniSiteSettings;
